/**
 * CloudWatch Integrations Configuration Dialog
 *
 * Unified modal for managing both Bedrock and SageMaker CloudWatch connections.
 * Tabs let the user choose which integration to add; the connection list shows both.
 */

import { ReactNode, useState } from "react";
import { Plus, CheckCircle2, AlertCircle, ChevronDown, ChevronUp, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { CloudWatchConnectionsList } from "./CloudWatchConnectionsList";
import { BedrockSetupInstructions } from "./BedrockSetupInstructions";
import { SageMakerSetupInstructions } from "./SageMakerSetupInstructions";
import {
  useBedrockSetupInstructions,
  useCreateBedrockConnection,
} from "@/hooks/useBedrock";
import {
  useSageMakerSetupInstructions,
  useCreateSageMakerConnection,
} from "@/hooks/useSagemaker";
import { AWS_BEDROCK_REGIONS } from "@/constants/aws";
import type { BedrockConnectionCreate } from "@/types/bedrock";
import type { SageMakerConnectionCreate } from "@/types/sagemaker";

interface CloudWatchConfigDialogProps {
  children: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const BEDROCK_DEFAULTS: Omit<BedrockConnectionCreate, "external_id"> = {
  connection_name: "",
  role_arn: "",
  aws_region: "us-east-1",
  log_group_name: "/aws/bedrock/modelinvocations",
};

const SAGEMAKER_DEFAULTS: Omit<SageMakerConnectionCreate, "external_id"> = {
  connection_name: "",
  role_arn: "",
  aws_region: "us-east-1",
  log_group_name: "/aws/sagemaker/Endpoints",
};

export function CloudWatchConfigDialog({
  children,
  open,
  onOpenChange,
}: CloudWatchConfigDialogProps) {
  const [activeTab, setActiveTab] = useState<"bedrock" | "sagemaker">("bedrock");
  const [showAddForm, setShowAddForm] = useState(false);
  const [showInstructions, setShowInstructions] = useState(true);

  const [bedrockForm, setBedrockForm] =
    useState<Omit<BedrockConnectionCreate, "external_id">>(BEDROCK_DEFAULTS);
  const [sagemakerForm, setSagemakerForm] =
    useState<Omit<SageMakerConnectionCreate, "external_id">>(SAGEMAKER_DEFAULTS);

  const { data: bedrockInstructions, isLoading: bedrockInstructionsLoading } =
    useBedrockSetupInstructions(showAddForm && activeTab === "bedrock");
  const { data: sagemakerInstructions, isLoading: sagemakerInstructionsLoading } =
    useSageMakerSetupInstructions(showAddForm && activeTab === "sagemaker");

  const bedrockCreate = useCreateBedrockConnection();
  const sagemakerCreate = useCreateSageMakerConnection();

  const handleReset = () => {
    setShowAddForm(false);
    setShowInstructions(true);
    setBedrockForm(BEDROCK_DEFAULTS);
    setSagemakerForm(SAGEMAKER_DEFAULTS);
    bedrockCreate.reset();
    sagemakerCreate.reset();
  };

  const handleBedrockSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!bedrockInstructions) return;
    try {
      await bedrockCreate.mutateAsync({
        ...bedrockForm,
        connection_name: bedrockForm.connection_name.trim(),
        role_arn: bedrockForm.role_arn.trim(),
        log_group_name: bedrockForm.log_group_name.trim(),
        external_id: bedrockInstructions.external_id,
      });
      setTimeout(() => handleReset(), 1500);
    } catch {
      // error rendered via bedrockCreate.error
    }
  };

  const handleSageMakerSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!sagemakerInstructions) return;
    try {
      await sagemakerCreate.mutateAsync({
        ...sagemakerForm,
        connection_name: sagemakerForm.connection_name.trim(),
        role_arn: sagemakerForm.role_arn.trim(),
        log_group_name: sagemakerForm.log_group_name.trim(),
        external_id: sagemakerInstructions.external_id,
      });
      setTimeout(() => handleReset(), 1500);
    } catch {
      // error rendered via sagemakerCreate.error
    }
  };

  const bedrockFormValid =
    bedrockForm.connection_name.trim() &&
    bedrockForm.role_arn.trim().startsWith("arn:aws:iam::") &&
    bedrockForm.log_group_name.trim() &&
    !!bedrockInstructions;

  const sagemakerFormValid =
    sagemakerForm.connection_name.trim() &&
    sagemakerForm.role_arn.trim().startsWith("arn:aws:iam::") &&
    sagemakerForm.log_group_name.trim() &&
    !!sagemakerInstructions;

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) handleReset();
        onOpenChange?.(nextOpen);
      }}
    >
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>CloudWatch Integrations</DialogTitle>
          <DialogDescription>
            Connect your AWS account to ingest model invocation logs from Bedrock or SageMaker for
            security monitoring
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Unified connections list */}
          <div>
            <h3 className="text-sm font-medium mb-3">Configured Connections</h3>
            <CloudWatchConnectionsList />
          </div>

          <Separator />

          {/* Add Connection */}
          <div>
            {!showAddForm ? (
              <Button
                variant="outline"
                className="w-full"
                onClick={() => setShowAddForm(true)}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Connection
              </Button>
            ) : (
              <div className="space-y-5">
                <h3 className="text-sm font-medium">Add New Connection</h3>

                <Tabs
                  value={activeTab}
                  onValueChange={(v) => {
                    setActiveTab(v as "bedrock" | "sagemaker");
                    setShowInstructions(true);
                    bedrockCreate.reset();
                    sagemakerCreate.reset();
                  }}
                >
                  <TabsList className="w-full">
                    <TabsTrigger value="bedrock" className="flex-1">
                      AWS Bedrock
                    </TabsTrigger>
                    <TabsTrigger value="sagemaker" className="flex-1">
                      AWS SageMaker
                    </TabsTrigger>
                  </TabsList>

                  {/* ── Bedrock tab ── */}
                  <TabsContent value="bedrock">
                    <form onSubmit={handleBedrockSubmit} className="space-y-5 pt-4">
                      <div className="rounded-lg border bg-muted/30">
                        <button
                          type="button"
                          className="flex w-full items-center justify-between px-4 py-3 text-sm font-medium"
                          onClick={() => setShowInstructions((v) => !v)}
                        >
                          <span>Step 1 — Configure AWS IAM Role</span>
                          {showInstructions ? (
                            <ChevronUp className="h-4 w-4 text-muted-foreground" />
                          ) : (
                            <ChevronDown className="h-4 w-4 text-muted-foreground" />
                          )}
                        </button>
                        {showInstructions && (
                          <div className="px-4 pb-4 border-t">
                            <BedrockSetupInstructions
                              setupInstructions={bedrockInstructions}
                              isLoading={bedrockInstructionsLoading}
                            />
                          </div>
                        )}
                      </div>

                      <ConnectionFormFields
                        idPrefix="cw-bedrock"
                        formData={bedrockForm}
                        onChange={setBedrockForm}
                        placeholders={{
                          name: "prod-bedrock",
                          arn: "arn:aws:iam::123456789012:role/VortalBedrockRole",
                          logGroup: "/aws/bedrock/modelinvocations",
                        }}
                        isSuccess={bedrockCreate.isSuccess}
                        isError={bedrockCreate.isError}
                        errorMessage={bedrockCreate.error?.message}
                        isPending={bedrockCreate.isPending}
                        isFormValid={!!bedrockFormValid}
                        onCancel={handleReset}
                      />
                    </form>
                  </TabsContent>

                  {/* ── SageMaker tab ── */}
                  <TabsContent value="sagemaker">
                    <form onSubmit={handleSageMakerSubmit} className="space-y-5 pt-4">
                      <div className="rounded-lg border bg-muted/30">
                        <button
                          type="button"
                          className="flex w-full items-center justify-between px-4 py-3 text-sm font-medium"
                          onClick={() => setShowInstructions((v) => !v)}
                        >
                          <span>Step 1 — Configure AWS IAM Role</span>
                          {showInstructions ? (
                            <ChevronUp className="h-4 w-4 text-muted-foreground" />
                          ) : (
                            <ChevronDown className="h-4 w-4 text-muted-foreground" />
                          )}
                        </button>
                        {showInstructions && (
                          <div className="px-4 pb-4 border-t">
                            <SageMakerSetupInstructions
                              setupInstructions={sagemakerInstructions}
                              isLoading={sagemakerInstructionsLoading}
                            />
                          </div>
                        )}
                      </div>

                      <ConnectionFormFields
                        idPrefix="cw-sagemaker"
                        formData={sagemakerForm}
                        onChange={setSagemakerForm}
                        placeholders={{
                          name: "prod-sagemaker",
                          arn: "arn:aws:iam::123456789012:role/VortalSageMakerRole",
                          logGroup: "/aws/sagemaker/Endpoints",
                        }}
                        isSuccess={sagemakerCreate.isSuccess}
                        isError={sagemakerCreate.isError}
                        errorMessage={sagemakerCreate.error?.message}
                        isPending={sagemakerCreate.isPending}
                        isFormValid={!!sagemakerFormValid}
                        onCancel={handleReset}
                      />
                    </form>
                  </TabsContent>
                </Tabs>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Shared form fields extracted to avoid duplication between the two tabs
// ---------------------------------------------------------------------------

interface FormData {
  connection_name: string;
  role_arn: string;
  aws_region: string;
  log_group_name: string;
}

interface ConnectionFormFieldsProps {
  idPrefix: string;
  formData: FormData;
  onChange: (data: FormData) => void;
  placeholders: { name: string; arn: string; logGroup: string };
  isSuccess: boolean;
  isError: boolean;
  errorMessage?: string;
  isPending: boolean;
  isFormValid: boolean;
  onCancel: () => void;
}

function ConnectionFormFields({
  idPrefix,
  formData,
  onChange,
  placeholders,
  isSuccess,
  isError,
  errorMessage,
  isPending,
  isFormValid,
  onCancel,
}: ConnectionFormFieldsProps) {
  return (
    <div className="space-y-4">
      <h4 className="text-sm font-medium text-muted-foreground">
        Step 2 — Enter Connection Details
      </h4>

      {isSuccess && (
        <div className="flex items-start gap-2 rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-800 dark:border-green-800 dark:bg-green-950 dark:text-green-200">
          <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium">Connection validated successfully!</p>
            <p className="text-xs mt-1">
              Vortal can now assume your IAM role and read from CloudWatch. The first data pull
              has been enqueued.
            </p>
          </div>
        </div>
      )}

      {isError && (
        <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800 dark:border-red-800 dark:bg-red-950 dark:text-red-200">
          <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium">Connection validation failed</p>
            <p className="text-xs mt-1">
              {errorMessage ||
                "Unable to assume the IAM role. Verify the role ARN, external ID, and trust policy."}
            </p>
          </div>
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-name`}>
          Connection Name <span className="text-destructive">*</span>
        </Label>
        <Input
          id={`${idPrefix}-name`}
          placeholder={placeholders.name}
          value={formData.connection_name}
          onChange={(e) => onChange({ ...formData, connection_name: e.target.value })}
          autoComplete="off"
          required
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-arn`}>
          IAM Role ARN <span className="text-destructive">*</span>
        </Label>
        <Input
          id={`${idPrefix}-arn`}
          placeholder={placeholders.arn}
          value={formData.role_arn}
          onChange={(e) => onChange({ ...formData, role_arn: e.target.value })}
          autoComplete="off"
          required
        />
        <p className="text-xs text-muted-foreground">
          The ARN of the cross-account IAM role you created in Step 1
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-region`}>AWS Region</Label>
          <Select
            value={formData.aws_region}
            onValueChange={(value) => onChange({ ...formData, aws_region: value })}
          >
            <SelectTrigger id={`${idPrefix}-region`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {AWS_BEDROCK_REGIONS.map((region) => (
                <SelectItem key={region} value={region}>
                  {region}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-log-group`}>
            Log Group Name <span className="text-destructive">*</span>
          </Label>
          <Input
            id={`${idPrefix}-log-group`}
            placeholder={placeholders.logGroup}
            value={formData.log_group_name}
            onChange={(e) => onChange({ ...formData, log_group_name: e.target.value })}
            autoComplete="off"
            required
          />
        </div>
      </div>

      <div className="flex gap-2 pt-1">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isPending}>
          Cancel
        </Button>
        <Button type="submit" disabled={!isFormValid || isPending}>
          {isPending ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Validating…
            </>
          ) : (
            "Add Connection"
          )}
        </Button>
      </div>
    </div>
  );
}
